import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectionStrategy, ElementRef, ViewChild, NgZone, effect, inject, untracked } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig} from '@angular/material/dialog';
import { ModalEmailComponent } from './modalEmail.component';
import { AuthService } from '../auth.service';
import swal from 'sweetalert2';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TemaService } from '../../shared/tema/tema.service';

declare var $: any;

/**
 * FONDO: constelaciones en 2D.
 *
 * Antes era una red neuronal en 3D (rotacion + proyeccion en perspectiva) y en
 * equipos modestos se entrecortaba. Las causas, y que se hizo con cada una:
 *
 *   - shadowBlur en cada pulso: de lo mas caro que tiene el canvas -> fuera;
 *     el titileo de las estrellas da la misma vida gratis
 *   - un sort() de todos los puntos POR CUADRO (algoritmo del pintor) -> se
 *     fue junto con el 3D
 *   - un beginPath/stroke POR CADA linea -> se agrupan por tono y se trazan
 *     en 8 tandas
 *   - canvas a la resolucion completa del monitor -> tope de dpr en 1.5; en
 *     un fondo difuso la diferencia no se ve
 *   - movimiento atado a los FPS -> delta de tiempo: igual de sereno a 60
 *     que a 144 Hz
 */
interface Estrella {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  /** Fase y velocidad del titileo, para que no parpadeen todas a la vez. */
  fase: number;
  titileo: number;
  brillo: number;
}

interface Fugaz {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** De 1 a 0; al llegar a 0 desaparece. */
  vida: number;
}

/** El trazo de una constelacion real, normalizado de 0 a 1. */
interface TrazoConstelacion {
  nombre: string;
  estrellas: number[][];
  lineas: number[][];
}

/** Una constelacion ya colocada en pantalla, en pixeles y con su ancla. */
interface ConstelacionPuesta {
  nombre: string;
  px: number[];
  py: number[];
  lineas: number[][];
  ax: number;
  ay: number;
  /** Fase de la respiracion del grupo, para que no se mezan todas juntas. */
  fase: number;
  radios: number[];
  fases: number[];
  titileos: number[];
  etiquetaY: number;

  /* ---- Ciclo de vida: se dibuja, se queda encendida y cede su lugar ---- */

  /** Su color propio, indice en la lista de colores de constelacion de la
   *  paleta: con el se va pintando conforme se forma. Es indice y no color
   *  para que, si cambia el tema, la figura tome el color del nuevo cielo. */
  tinta: number;
  /** 0 = dibujandose, 1 = encendida, 2 = apagandose. */
  etapa: number;
  /** Cuando empezo la etapa actual y cuanto dura, en ms. */
  desde: number;
  dura: number;
  /** Ranura de pantalla que ocupa; al irse, ahi entra la siguiente. */
  ranura: number;
  /** Largo de cada linea y total, para repartir el trazado por distancia
   *  real y que la mano dibuje parejo, no a saltos por segmento. */
  largos: number[];
  largoTotal: number;
  /** En que punto del trazo (0 a 1) el lapiz alcanza cada estrella. */
  aparece: number[];
}

/**
 * Los colores del cielo tal como los declara el CSS (variables --login-* de
 * login.component.css), en canales 'r, g, b'.
 */
interface ColoresCielo {
  red: string;
  redAlfa: number;
  cursor: string;
  cursorAlfa: number;
  estrella: string;
  estrellaAlfa: number;
  halo: string;
  fugazCola: string;
  fugaz: string;
  fugazCabeza: string;
  constelaciones: string[];
  nucleo: string;
}

/**
 * Los mismos colores ya armados para el canvas: las tandas de las lineas y
 * prefijos 'rgba(r, g, b, ' a los que solo les falta el alfa, para que el
 * animate no lea estilos ni arme mas cadenas que las de siempre.
 */
interface PaletaCielo {
  linea: string[];
  lineaCursor: string[];
  halo: string;
  estrella: string;
  estrellaAlfa: number;
  fugazCola: string;
  fugaz: string;
  fugazCabeza: string;
  /** Canales 'r, g, b' de cada color de constelacion. */
  constelaciones: string[];
  constelacionNucleo: string;
}

@Component({
    selector: 'app-login',
    standalone: true,
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
      FormsModule,
      ReactiveFormsModule,
      CommonModule,
      MatFormFieldModule,
      MatExpansionModule,
      MatIconModule
    ],
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  fechahoy: Date = new Date();
  mostrarPassword = false;

  /* ---------------- Recordarme ----------------
   *
   * OJO: la contraseña se guarda TAL CUAL en localStorage. No hay forma de
   * guardarla de manera segura en el navegador —cifrarla aquí no serviría,
   * porque la llave viviría en el mismo lugar—, así que quien tenga acceso a
   * esta computadora o al inspector del navegador puede leerla. Por eso es una
   * casilla apagada por omisión y no un comportamiento automático.
   *
   * La llave NO la borra el logout: auth.service sólo quita CosData, CosTk y
   * menuItems. Eso es a propósito: si cerrar sesión olvidara los datos, la
   * casilla no serviría de nada.
   */
  private static readonly LLAVE_RECORDAR = 'cos.login.recordar';

  recordar = false;
  usuarioValor = '';
  passwordValor = '';

  @ViewChild('neuralCanvas') canvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('password') txtPass: ElementRef;
  @ViewChild('usuario') txtUser: ElementRef;

  private ctx: CanvasRenderingContext2D | null = null;
  private estrellas: Estrella[] = [];
  private fugaces: Fugaz[] = [];
  private constelaciones: ConstelacionPuesta[] = [];
  private proximaFugaz = 0;
  private ultimoCuadro = 0;
  private animationId = 0;
  private mouse = { x: -1000, y: -1000 };
  private cw = 0;
  private ch = 0;

  /**
   * El cielo de siempre: la noche vino de Solser. Son los valores por
   * omision de las variables --login-* del CSS, y tambien lo que se usa si
   * alguna falta o no se entiende.
   *
   * Las constelaciones se pintan con tonos de estrella (azul hielo,
   * turquesa, dorado...) que resaltan sobre el fondo granate sin pelearse
   * con el: el rojo se queda para el campo de estrellas suelto.
   */
  private static readonly NOCHE: ColoresCielo = {
    red: '214, 70, 84',
    redAlfa: 0.45,
    cursor: '255, 96, 104',
    cursorAlfa: 0.55,
    estrella: '244, 218, 222',
    estrellaAlfa: 0.9,
    halo: '224, 86, 100',
    fugazCola: '255, 190, 195',
    fugaz: '255, 224, 228',
    fugazCabeza: '255, 236, 238',
    constelaciones: [
      '176, 216, 255',
      '140, 238, 226',
      '255, 212, 138',
      '206, 172, 255',
      '255, 156, 170',
      '162, 255, 194',
    ],
    nucleo: '255, 252, 250',
  };

  /** Tonos precalculados y arreglos reutilizados cuadro a cuadro: cero
   *  cadenas y cero arreglos nuevos por cuadro, para no despertar al
   *  recolector de basura a media animacion. La paleta se rearma solo al
   *  iniciar y al cambiar de tema (leerPaleta). */
  private paleta: PaletaCielo = LoginComponent.armarPaleta(LoginComponent.NOCHE);
  private readonly tema = inject(TemaService);
  private readonly segmentos: number[][] = Array.from({ length: 8 }, () => []);
  private readonly segmentosMouse: number[][] = Array.from({ length: 8 }, () => []);

  private readonly onResize = () => this.setupCanvas();
  private readonly onMouseMove = (e: MouseEvent) => {
    const canvas = this.canvasRef && this.canvasRef.nativeElement;
    if (!canvas) { return; }
    const rect = canvas.getBoundingClientRect();
    this.mouse.x = e.clientX - rect.left;
    this.mouse.y = e.clientY - rect.top;
  };
  private readonly onMouseLeave = () => {
    this.mouse.x = -1000;
    this.mouse.y = -1000;
  };

  constructor(
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog,
    private zone: NgZone) {
      if (this.authService.isAuthenticated()) {
        this.router.navigate(['dashboard']);
      }
      // Al cambiar de tema (en automatico, cuando cambia el sistema) el cielo
      // relee sus colores. Cuando corre el efecto el TemaService ya dejo los
      // atributos en <html>. Si aun no hay canvas no hace nada: al iniciar la
      // lee ngAfterViewInit.
      effect(() => {
        this.tema.actual();
        untracked(() => this.leerPaleta());
      });
  }

  ngOnInit() {
      const body = document.getElementsByTagName('body')[0];
      body.classList.add('login-page');
      body.classList.add('off-canvas-sidebar');
      this.cargarRecordado();
  }

  /** Lo guardado la última vez, si la casilla estaba encendida. */
  private cargarRecordado(): void {
    let guardado: string | null = null;
    try {
      guardado = localStorage.getItem(LoginComponent.LLAVE_RECORDAR);
      if (!guardado) {
        return;
      }
      const datos = JSON.parse(guardado);
      this.usuarioValor = (datos && datos.usuario) || '';
      this.passwordValor = (datos && datos.password) || '';
      // Sólo se marca si de verdad hay algo que recordar: con la casilla
      // encendida y los campos vacíos, la pantalla mentiría.
      this.recordar = !!this.usuarioValor;
    } catch (e) {
      // Si quedó algo ilegible se descarta. Vale más entrar con los campos
      // vacíos que dejar la pantalla de acceso rota.
      localStorage.removeItem(LoginComponent.LLAVE_RECORDAR);
      this.usuarioValor = '';
      this.passwordValor = '';
      this.recordar = false;
    }
  }

  /**
   * Al apagar la casilla se borra en ese momento, no hasta el siguiente
   * ingreso: si alguien la apaga y se va sin entrar, sus datos no deben
   * quedarse guardados.
   */
  alternarRecordar(): void {
    if (!this.recordar) {
      localStorage.removeItem(LoginComponent.LLAVE_RECORDAR);
    }
  }

  private guardarRecordado(usuario: string, password: string): void {
    if (!this.recordar) {
      localStorage.removeItem(LoginComponent.LLAVE_RECORDAR);
      return;
    }
    try {
      localStorage.setItem(LoginComponent.LLAVE_RECORDAR,
        JSON.stringify({ usuario: usuario, password: password }));
    } catch (e) {
      // Si el navegador no deja escribir (modo privado, cuota llena), no se
      // recuerda y ya: no es motivo para impedir el acceso.
    }
  }

  ngAfterViewInit() {
    this.leerPaleta();
    this.setupCanvas();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseout', this.onMouseLeave);
    this.zone.runOutsideAngular(() => {
      this.animationId = requestAnimationFrame(this.animate);
    });
  }

  ngOnDestroy(){
    cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseout', this.onMouseLeave);

    const body = document.getElementsByTagName('body')[0];
    body.classList.remove('login-page');
    body.classList.remove('off-canvas-sidebar');
  }

  /* ===== Neural network background ===== */
  private setupCanvas() {
    const canvas = this.canvasRef && this.canvasRef.nativeElement;
    if (!canvas) { return; }
    // Tope al dpr: en un monitor con escala 200% el canvas a resolución
    // completa cuadruplica el trabajo de relleno, y en un fondo difuso la
    // nitidez extra no se distingue.
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const w = window.innerWidth;
    const h = Math.max(window.innerHeight, canvas.clientHeight || 0);
    this.cw = w;
    this.ch = h;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    this.ctx = canvas.getContext('2d');
    if (this.ctx) {
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    this.crearEstrellas(w, h);
  }

  private crearEstrellas(w: number, h: number) {
    // Menos puntos y más lentos que la versión anterior: una constelación es
    // deriva serena, no un enjambre. Además el costo de las líneas crece con
    // el CUADRADO de los puntos, así que aquí es donde se gana fluidez.
    // Un poco menos densas que antes: las figuras con nombre son ahora las
    // protagonistas y el campo libre es su fondo.
    const cuantas = Math.min(88, Math.max(36, Math.round((w * h) / 20000)));
    this.estrellas = [];
    for (let i = 0; i < cuantas; i++) {
      const ang = Math.random() * Math.PI * 2;
      const vel = 0.05 + Math.random() * 0.16;
      const lucero = Math.random() < 0.16;
      this.estrellas.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: Math.cos(ang) * vel,
        vy: Math.sin(ang) * vel,
        r: lucero ? 2.3 + Math.random() * 1.2 : 1.15 + Math.random() * 1.0,
        fase: Math.random() * Math.PI * 2,
        titileo: 0.0006 + Math.random() * 0.0012,
        brillo: lucero ? 0.95 : 0.55 + Math.random() * 0.35,
      });
    }
    this.colocarConstelaciones(w, h);
  }

  /**
   * Los trazos clasicos, en un lienzo normalizado de 0 a 1 (y hacia abajo,
   * como el canvas). "lineas" une indices de "estrellas": son las varillas
   * del dibujo de atlas, no lineas de cercania. Estilizados, pero con la
   * forma por la que cada una se reconoce: el reloj de arena de Orion, el
   * cazo de la Osa, la W de Casiopea, el cuadrante de Pegaso...
   */
  private static readonly TRAZOS: TrazoConstelacion[] = [
    {
      nombre: 'ORIÓN',
      estrellas: [[0.30, 0.16], [0.68, 0.20], [0.42, 0.50], [0.50, 0.52], [0.58, 0.54], [0.36, 0.86], [0.72, 0.84]],
      lineas: [[0, 1], [0, 2], [1, 4], [2, 3], [3, 4], [2, 5], [4, 6]],
    },
    {
      nombre: 'OSA MAYOR',
      estrellas: [[0.04, 0.34], [0.22, 0.24], [0.38, 0.30], [0.52, 0.34], [0.74, 0.26], [0.78, 0.52], [0.56, 0.58]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 3]],
    },
    {
      nombre: 'CASIOPEA',
      estrellas: [[0.06, 0.42], [0.28, 0.20], [0.50, 0.44], [0.72, 0.16], [0.94, 0.34]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 4]],
    },
    {
      nombre: 'PEGASO',
      estrellas: [[0.86, 0.22], [0.58, 0.16], [0.60, 0.46], [0.88, 0.50], [0.44, 0.56], [0.32, 0.60], [0.14, 0.66], [0.42, 0.08]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 0], [2, 4], [4, 5], [5, 6], [1, 7]],
    },
    {
      nombre: 'ÁGUILA',
      estrellas: [[0.50, 0.18], [0.58, 0.30], [0.66, 0.42], [0.42, 0.44], [0.16, 0.34], [0.50, 0.64], [0.64, 0.80], [0.28, 0.76]],
      lineas: [[0, 1], [1, 2], [1, 3], [3, 4], [3, 5], [5, 6], [5, 7]],
    },
    {
      nombre: 'VIRGO',
      estrellas: [[0.56, 0.88], [0.48, 0.56], [0.58, 0.40], [0.76, 0.24], [0.30, 0.50], [0.14, 0.58], [0.72, 0.56]],
      lineas: [[0, 1], [1, 2], [2, 3], [1, 4], [4, 5], [2, 6], [6, 0]],
    },
    {
      nombre: 'CISNE',
      estrellas: [[0.50, 0.10], [0.50, 0.44], [0.50, 0.66], [0.50, 0.88], [0.22, 0.28], [0.78, 0.60]],
      lineas: [[0, 1], [1, 2], [2, 3], [1, 4], [1, 5]],
    },
    {
      nombre: 'LEO',
      estrellas: [[0.28, 0.72], [0.26, 0.52], [0.34, 0.40], [0.30, 0.26], [0.18, 0.18], [0.08, 0.28], [0.68, 0.38], [0.66, 0.60], [0.90, 0.48]],
      lineas: [[5, 4], [4, 3], [3, 2], [2, 1], [1, 0], [2, 6], [6, 8], [8, 7], [7, 0], [6, 7]],
    },
    {
      nombre: 'ANDRÓMEDA',
      estrellas: [[0.10, 0.62], [0.28, 0.54], [0.46, 0.46], [0.64, 0.36], [0.82, 0.24], [0.40, 0.68], [0.58, 0.60]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 4], [2, 5], [3, 6]],
    },
    {
      nombre: 'OSA MENOR',
      estrellas: [[0.08, 0.26], [0.24, 0.38], [0.42, 0.48], [0.58, 0.58], [0.76, 0.50], [0.82, 0.70], [0.62, 0.76]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 3]],
    },
    {
      nombre: 'ESCORPIÓN',
      estrellas: [[0.10, 0.14], [0.24, 0.24], [0.18, 0.36], [0.34, 0.34], [0.44, 0.48], [0.52, 0.62], [0.62, 0.74], [0.76, 0.78], [0.88, 0.70], [0.88, 0.56]],
      lineas: [[0, 2], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9]],
    },
    {
      nombre: 'TAURO',
      estrellas: [[0.10, 0.70], [0.28, 0.58], [0.46, 0.50], [0.64, 0.36], [0.84, 0.20], [0.60, 0.64], [0.80, 0.74], [0.38, 0.32]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 4], [2, 5], [5, 6], [2, 7]],
    },
    {
      nombre: 'GÉMINIS',
      estrellas: [[0.22, 0.12], [0.30, 0.34], [0.24, 0.56], [0.14, 0.80], [0.38, 0.78], [0.68, 0.16], [0.62, 0.40], [0.72, 0.62], [0.60, 0.86], [0.84, 0.82]],
      lineas: [[0, 1], [1, 2], [2, 3], [2, 4], [1, 6], [5, 6], [6, 7], [7, 8], [7, 9]],
    },
    {
      nombre: 'CAN MAYOR',
      estrellas: [[0.18, 0.18], [0.34, 0.36], [0.28, 0.58], [0.50, 0.46], [0.66, 0.34], [0.74, 0.60], [0.56, 0.76], [0.40, 0.84]],
      lineas: [[0, 1], [1, 2], [1, 3], [3, 4], [3, 5], [5, 6], [6, 7], [2, 7]],
    },
    {
      nombre: 'PERSEO',
      estrellas: [[0.14, 0.16], [0.30, 0.32], [0.44, 0.48], [0.58, 0.64], [0.74, 0.80], [0.34, 0.66], [0.22, 0.82], [0.64, 0.32]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 4], [2, 5], [5, 6], [2, 7]],
    },
    {
      nombre: 'LIRA',
      estrellas: [[0.28, 0.14], [0.44, 0.34], [0.34, 0.56], [0.58, 0.44], [0.50, 0.70], [0.68, 0.60]],
      lineas: [[0, 1], [1, 2], [2, 4], [4, 5], [5, 3], [3, 1]],
    },
    {
      nombre: 'DRACO',
      estrellas: [[0.06, 0.82], [0.22, 0.68], [0.38, 0.72], [0.50, 0.56], [0.44, 0.38], [0.58, 0.26], [0.76, 0.22], [0.88, 0.34], [0.80, 0.46]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 5]],
    },
    {
      nombre: 'BOYERO',
      estrellas: [[0.50, 0.92], [0.38, 0.66], [0.28, 0.44], [0.46, 0.26], [0.64, 0.18], [0.72, 0.42], [0.62, 0.62]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 1]],
    },
    {
      nombre: 'AURIGA',
      estrellas: [[0.30, 0.14], [0.62, 0.18], [0.78, 0.44], [0.58, 0.72], [0.28, 0.64], [0.16, 0.38]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0]],
    },
    {
      nombre: 'CRUZ DEL SUR',
      estrellas: [[0.50, 0.08], [0.50, 0.88], [0.20, 0.46], [0.80, 0.38]],
      lineas: [[0, 1], [2, 3]],
    },
    {
      nombre: 'SAGITARIO',
      estrellas: [[0.14, 0.44], [0.30, 0.26], [0.46, 0.36], [0.60, 0.22], [0.74, 0.40], [0.66, 0.64], [0.44, 0.72], [0.24, 0.64], [0.88, 0.54]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 0], [4, 8], [8, 5]],
    },
    {
      nombre: 'DELFÍN',
      estrellas: [[0.32, 0.28], [0.50, 0.18], [0.62, 0.34], [0.46, 0.46], [0.66, 0.66]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 0], [3, 4]],
    },
    {
      nombre: 'CORONA BOREAL',
      estrellas: [[0.12, 0.54], [0.24, 0.34], [0.40, 0.22], [0.58, 0.22], [0.76, 0.34], [0.88, 0.52]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5]],
    },
    {
      nombre: 'CÁNCER',
      estrellas: [[0.50, 0.40], [0.34, 0.20], [0.68, 0.22], [0.56, 0.64], [0.74, 0.82]],
      lineas: [[1, 0], [2, 0], [0, 3], [3, 4]],
    },
    {
      nombre: 'ARIES',
      estrellas: [[0.14, 0.64], [0.36, 0.50], [0.60, 0.38], [0.84, 0.32]],
      lineas: [[0, 1], [1, 2], [2, 3]],
    },
    {
      nombre: 'CEFEO',
      estrellas: [[0.28, 0.72], [0.28, 0.42], [0.50, 0.16], [0.72, 0.42], [0.72, 0.72]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 0], [1, 3]],
    },
    {
      nombre: 'HÉRCULES',
      estrellas: [[0.36, 0.34], [0.60, 0.30], [0.66, 0.52], [0.40, 0.56], [0.24, 0.16], [0.76, 0.14], [0.28, 0.78], [0.74, 0.76]],
      lineas: [[0, 1], [1, 2], [2, 3], [3, 0], [0, 4], [1, 5], [3, 6], [2, 7]],
    },
  ];

  /**
   * Donde puede caer una constelacion: las cuatro esquinas. El centro es de
   * la tarjeta del formulario, y una figura a medias detras del vidrio no se
   * reconoceria.
   */
  private static readonly RANURAS: number[][] = [
    [0.14, 0.26], [0.86, 0.24], [0.12, 0.74], [0.88, 0.72],
    // Los costados a media altura: el formulario ocupa el centro, pero a los
    // lados queda sitio y así las figuras no caen siempre en las esquinas.
    [0.07, 0.50], [0.93, 0.50],
  ];

  private colocarConstelaciones(w: number, h: number) {
    this.constelaciones = [];
    const cuantas = Math.min(4, Math.max(2, Math.floor(w / 480)));
    const ranuras = LoginComponent.barajar(
      LoginComponent.RANURAS.map((_, i) => i));
    const t = performance.now();

    for (let i = 0; i < cuantas && i < ranuras.length; i++) {
      const c = this.nuevaConstelacion(ranuras[i], w, h, t, []);
      if (!c) { continue; }
      // Escalonadas: al abrir la pantalla no se dibujan todas a la vez, van
      // apareciendo una tras otra.
      c.desde = t - i * 1100;
      this.constelaciones.push(c);
    }
  }

  /**
   * Arma una figura para una ranura: elige un trazo que no este ya en
   * pantalla, lo gira un poco, lo encaja en la ranura y precalcula lo que el
   * dibujado progresivo necesita (largo de cada linea y en que momento del
   * trazo aparece cada estrella).
   */
  private nuevaConstelacion(ranura: number, w: number, h: number, t: number,
                            ocupados: string[]): ConstelacionPuesta | null {
    const libres = LoginComponent.TRAZOS.filter(
      (tz) => ocupados.indexOf(tz.nombre) < 0);
    const candidatos = libres.length ? libres : LoginComponent.TRAZOS;
    const tz = candidatos[Math.floor(Math.random() * candidatos.length)];
    if (!tz) { return null; }

    const escala = Math.min(w, h) * 0.30;
    // Un giro leve (±12°) para que no salgan alineadas como en un catalogo;
    // mas giro y el cazo de la Osa deja de leerse como cazo.
    const giro = (Math.random() - 0.5) * 0.42;
    const cos = Math.cos(giro);
    const sin = Math.sin(giro);
    const px: number[] = [];
    const py: number[] = [];
    for (const e of tz.estrellas) {
      const dx = (e[0] - 0.5) * escala;
      const dy = (e[1] - 0.5) * escala;
      px.push(dx * cos - dy * sin);
      py.push(dx * sin + dy * cos);
    }

    // Que la figura completa quepa en pantalla, con su etiqueta.
    const margen = 26;
    const punto = LoginComponent.RANURAS[ranura];
    let ax = punto[0] * w;
    let ay = punto[1] * h;
    ax = Math.min(Math.max(ax, margen - Math.min(...px)), w - margen - Math.max(...px));
    ay = Math.min(Math.max(ay, margen - Math.min(...py)), h - margen - 22 - Math.max(...py));

    const radios: number[] = [];
    const fases: number[] = [];
    const titileos: number[] = [];
    for (let k = 0; k < px.length; k++) {
      radios.push(1.8 + Math.random() * 1.1);
      fases.push(Math.random() * Math.PI * 2);
      titileos.push(0.0004 + Math.random() * 0.0008);
    }

    // Largo real de cada linea: el trazo avanza a velocidad pareja, no un
    // segmento por tiempo (si no, una linea larga se dibujaria de golpe).
    const largos: number[] = [];
    let largoTotal = 0;
    for (const ln of tz.lineas) {
      const dx = px[ln[1]] - px[ln[0]];
      const dy = py[ln[1]] - py[ln[0]];
      const largo = Math.sqrt(dx * dx + dy * dy) || 1;
      largos.push(largo);
      largoTotal += largo;
    }

    // Cada estrella se enciende cuando el trazo la alcanza; las que nunca
    // toca una linea salen desde el principio.
    const aparece: number[] = px.map(() => 0);
    const tocada: boolean[] = px.map(() => false);
    let acumulado = 0;
    for (let i = 0; i < tz.lineas.length; i++) {
      const a = tz.lineas[i][0];
      const b = tz.lineas[i][1];
      if (!tocada[a]) { tocada[a] = true; aparece[a] = acumulado / largoTotal; }
      acumulado += largos[i];
      if (!tocada[b]) { tocada[b] = true; aparece[b] = acumulado / largoTotal; }
    }

    return {
      nombre: tz.nombre,
      px: px,
      py: py,
      lineas: tz.lineas,
      ax: ax,
      ay: ay,
      fase: Math.random() * Math.PI * 2,
      radios: radios,
      fases: fases,
      titileos: titileos,
      etiquetaY: Math.max(...py) + 22,
      tinta: crypto.getRandomValues(new Uint32Array(1))[0] % this.paleta.constelaciones.length,
      etapa: 0,
      desde: t,
      dura: 2600 + Math.random() * 1600,
      ranura: ranura,
      largos: largos,
      largoTotal: largoTotal,
      aparece: aparece,
    };
  }

  /**
   * Hace correr el reloj de cada figura: termina de dibujarse, se queda un
   * rato encendida, se apaga y deja su ranura a otra constelacion distinta.
   */
  private relevoConstelaciones(t: number, w: number, h: number) {
    for (let i = 0; i < this.constelaciones.length; i++) {
      const c = this.constelaciones[i];
      if (t - c.desde < c.dura) { continue; }

      if (c.etapa === 0) {
        c.etapa = 1;
        c.desde = t;
        c.dura = 9000 + Math.random() * 6000;
      } else if (c.etapa === 1) {
        c.etapa = 2;
        c.desde = t;
        c.dura = 2000;
      } else {
        const ocupados: string[] = [];
        for (const otra of this.constelaciones) {
          if (otra !== c) { ocupados.push(otra.nombre); }
        }
        const nueva = this.nuevaConstelacion(c.ranura, w, h, t, ocupados);
        if (nueva) {
          this.constelaciones[i] = nueva;
        } else {
          // Sin recambio disponible, esta misma se vuelve a dibujar.
          c.etapa = 0;
          c.desde = t;
          c.dura = 2600 + Math.random() * 1600;
        }
      }
    }
  }

  private static barajar<T>(lista: T[]): T[] {
    for (let i = lista.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = lista[i];
      lista[i] = lista[j];
      lista[j] = tmp;
    }
    return lista;
  }

  /** 8 tonos precalculados, del más tenue al más marcado. Las líneas se
   *  agrupan por tono y se trazan en 8 tandas, no una por una. */
  private static tandasDeColor(rgb: string, tope: number): string[] {
    const colores: string[] = [];
    for (let i = 0; i < 8; i++) {
      colores.push('rgba(' + rgb + ', ' + (((i + 1) / 8) * tope).toFixed(3) + ')');
    }
    return colores;
  }

  /**
   * Lee los colores del cielo de las variables --login-* del CSS y rearma la
   * paleta del canvas. Corre al iniciar y cada vez que cambia el tema, nunca
   * por cuadro. Se leen del canvas, que las hereda de .login-root.
   */
  private leerPaleta(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) {
      return;
    }
    const estilo = getComputedStyle(canvas);
    const noche = LoginComponent.NOCHE;
    const rgb = (nombre: string, defecto: string) => LoginComponent.leerCanales(estilo, nombre, defecto);
    const alfa = (nombre: string, defecto: number) => LoginComponent.leerAlfa(estilo, nombre, defecto);
    const constelaciones = estilo.getPropertyValue('--login-constelaciones')
      .split('/')
      .map((tinta) => LoginComponent.canales(tinta))
      .filter((tinta) => tinta !== '');
    this.paleta = LoginComponent.armarPaleta({
      red: rgb('--login-red-rgb', noche.red),
      redAlfa: alfa('--login-red-alfa', noche.redAlfa),
      cursor: rgb('--login-red-cursor-rgb', noche.cursor),
      cursorAlfa: alfa('--login-red-cursor-alfa', noche.cursorAlfa),
      estrella: rgb('--login-estrella-rgb', noche.estrella),
      estrellaAlfa: alfa('--login-estrella-alfa', noche.estrellaAlfa),
      halo: rgb('--login-estrella-halo-rgb', noche.halo),
      fugazCola: rgb('--login-fugaz-cola-rgb', noche.fugazCola),
      fugaz: rgb('--login-fugaz-rgb', noche.fugaz),
      fugazCabeza: rgb('--login-fugaz-cabeza-rgb', noche.fugazCabeza),
      constelaciones: constelaciones.length > 0 ? constelaciones : noche.constelaciones,
      nucleo: rgb('--login-constelacion-nucleo-rgb', noche.nucleo),
    });
  }

  private static leerCanales(estilo: CSSStyleDeclaration, nombre: string, defecto: string): string {
    return LoginComponent.canales(estilo.getPropertyValue(nombre)) || defecto;
  }

  private static leerAlfa(estilo: CSSStyleDeclaration, nombre: string, defecto: number): number {
    const valor = Number.parseFloat(estilo.getPropertyValue(nombre));
    return Number.isFinite(valor) ? valor : defecto;
  }

  /**
   * Un color del CSS como canales 'r, g, b', separados como siempre para que
   * las cadenas del canvas salgan identicas. Acepta canales sueltos
   * ('214, 70, 84', como los --cos-*-rgb) o un hexadecimal de seis cifras
   * (como --cos-texto). Devuelve cadena vacia si no es ninguno de los dos.
   */
  private static canales(valor: string): string {
    const limpio = valor.trim();
    if (/^#[\da-f]{6}$/i.test(limpio)) {
      return [1, 3, 5].map((i) => Number.parseInt(limpio.slice(i, i + 2), 16)).join(', ');
    }
    const partes = limpio.split(',').map((parte) => parte.trim());
    const valido = partes.length === 3 && partes.every((parte) => /^\d{1,3}$/.test(parte));
    return valido ? partes.join(', ') : '';
  }

  private static armarPaleta(colores: ColoresCielo): PaletaCielo {
    return {
      linea: LoginComponent.tandasDeColor(colores.red, colores.redAlfa),
      lineaCursor: LoginComponent.tandasDeColor(colores.cursor, colores.cursorAlfa),
      halo: 'rgba(' + colores.halo + ', ',
      estrella: 'rgba(' + colores.estrella + ', ',
      estrellaAlfa: colores.estrellaAlfa,
      fugazCola: 'rgba(' + colores.fugazCola + ', 0)',
      fugaz: 'rgba(' + colores.fugaz + ', ',
      fugazCabeza: 'rgba(' + colores.fugazCabeza + ', ',
      constelaciones: colores.constelaciones,
      constelacionNucleo: 'rgba(' + colores.nucleo + ', ',
    };
  }

  private animate = (ahora?: number) => {
    const canvas = this.canvasRef && this.canvasRef.nativeElement;
    const ctx = this.ctx;
    if (!canvas || !ctx) { return; }

    const t = ahora === undefined ? performance.now() : ahora;
    const paleta = this.paleta;
    // Movimiento por TIEMPO, no por cuadro: a 144 Hz se ve igual de sereno
    // que a 60. El tope de 3 evita el teletransporte al volver de una
    // pestaña en segundo plano, donde el navegador congela la animación.
    const dt = this.ultimoCuadro ? Math.min(3, (t - this.ultimoCuadro) / 16.667) : 1;
    this.ultimoCuadro = t;

    const w = this.cw;
    const h = this.ch;
    const maxD = 130;
    const maxD2 = maxD * maxD;
    const radioMouse = 170;
    const radioMouse2 = radioMouse * radioMouse;
    const margen = 40;

    ctx.clearRect(0, 0, w, h);

    // --- deriva ---
    for (const e of this.estrellas) {
      e.x += e.vx * dt;
      e.y += e.vy * dt;
      // Se envuelve por el lado contrario en vez de rebotar: el rebote se
      // notaba como un "golpe" en los bordes y rompía la calma.
      if (e.x < -margen) { e.x = w + margen; }
      else if (e.x > w + margen) { e.x = -margen; }
      if (e.y < -margen) { e.y = h + margen; }
      else if (e.y > h + margen) { e.y = -margen; }
    }

    // --- líneas de constelación, agrupadas por tono ---
    for (const s of this.segmentos) { s.length = 0; }
    const n = this.estrellas.length;
    for (let i = 0; i < n; i++) {
      const a = this.estrellas[i];
      for (let j = i + 1; j < n; j++) {
        const b = this.estrellas[j];
        const dx = a.x - b.x;
        if (dx > maxD || dx < -maxD) { continue; }
        const dy = a.y - b.y;
        if (dy > maxD || dy < -maxD) { continue; }
        const d2 = dx * dx + dy * dy;
        if (d2 < maxD2) {
          // Sin raíz cuadrada: 1 - d²/máx² da la misma curva suave de
          // desvanecimiento y se ahorra el sqrt de cada pareja.
          const cerca = 1 - d2 / maxD2;
          const tanda = Math.min(7, (cerca * 8) | 0);
          this.segmentos[tanda].push(a.x, a.y, b.x, b.y);
        }
      }
    }
    ctx.lineWidth = 0.8;
    for (let tanda = 0; tanda < 8; tanda++) {
      const seg = this.segmentos[tanda];
      if (!seg.length) { continue; }
      ctx.strokeStyle = paleta.linea[tanda];
      ctx.beginPath();
      for (let k = 0; k < seg.length; k += 4) {
        ctx.moveTo(seg[k], seg[k + 1]);
        ctx.lineTo(seg[k + 2], seg[k + 3]);
      }
      ctx.stroke();
    }

    // --- hilos hacia el cursor ---
    if (this.mouse.x >= 0) {
      for (const s of this.segmentosMouse) { s.length = 0; }
      for (const e of this.estrellas) {
        const dx = e.x - this.mouse.x;
        const dy = e.y - this.mouse.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < radioMouse2) {
          const cerca = 1 - d2 / radioMouse2;
          const tanda = Math.min(7, (cerca * 8) | 0);
          this.segmentosMouse[tanda].push(e.x, e.y);
        }
      }
      ctx.lineWidth = 0.7;
      for (let tanda = 0; tanda < 8; tanda++) {
        const seg = this.segmentosMouse[tanda];
        if (!seg.length) { continue; }
        ctx.strokeStyle = paleta.lineaCursor[tanda];
        ctx.beginPath();
        for (let k = 0; k < seg.length; k += 2) {
          ctx.moveTo(seg[k], seg[k + 1]);
          ctx.lineTo(this.mouse.x, this.mouse.y);
        }
        ctx.stroke();
      }
    }

    // --- estrellas: halo tenue + núcleo, con titileo ---
    // El titileo sustituye a los pulsos con shadowBlur de la versión 3D:
    // da la misma sensación de vida y no cuesta casi nada.
    for (const e of this.estrellas) {
      const alfa = e.brillo * (0.72 + 0.28 * Math.sin(t * e.titileo + e.fase));
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.r * 2.4, 0, 6.2832);
      ctx.fillStyle = paleta.halo + (0.1 * alfa).toFixed(3) + ')';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.r, 0, 6.2832);
      ctx.fillStyle = paleta.estrella + (paleta.estrellaAlfa * alfa).toFixed(3) + ')';
      ctx.fill();
    }

    // --- constelaciones con nombre ---
    // Grupos RIGIDOS: no derivan con el campo ni entran en las lineas de
    // cercania; solo respiran unos pixeles alrededor de su ancla. Si
    // derivaran, en un minuto la Osa Mayor seria un manchon cualquiera.
    // Cada una se dibuja trazo a trazo pintandose de SU color, se queda un
    // rato encendida —con un destello al cerrar la figura— y luego se apaga
    // para dejarle la ranura a otra constelacion distinta.
    this.relevoConstelaciones(t, w, h);

    ctx.textAlign = 'center';
    ctx.font = '600 11px Roboto, Arial, sans-serif';
    for (const c of this.constelaciones) {
      const ox = c.ax + Math.sin(t * 0.00006 + c.fase) * 9;
      const oy = c.ay + Math.cos(t * 0.00005 + c.fase) * 7;
      const paso = t - c.desde;
      // Cuanto del trazo lleva dibujado y con cuanta fuerza se ve el grupo.
      const avance = c.etapa === 0 ? Math.min(1, paso / c.dura) : 1;
      const alfaGrupo = c.etapa === 2 ? Math.max(0, 1 - paso / c.dura) : 1;
      // Destello del momento en que la figura queda cerrada.
      const chispa = c.etapa === 1 ? Math.max(0, 1 - paso / 900) : 0;
      const rgb = paleta.constelaciones[c.tinta % paleta.constelaciones.length];

      // --- lineas, hasta donde llegó el lápiz ---
      ctx.strokeStyle = 'rgba(' + rgb + ', ' + (alfaGrupo * (0.40 + chispa * 0.42)).toFixed(3) + ')';
      ctx.lineWidth = 1.05 + chispa * 0.75;
      if (c.etapa === 1) {
        // El halo sólo cuando ya está formada: es el premio visual de
        // haberla completado, y son cuatro trazos, no cuesta nada.
        ctx.shadowColor = 'rgba(' + rgb + ', 0.75)';
        ctx.shadowBlur = 7 + chispa * 13;
      }
      ctx.beginPath();
      const objetivo = avance * c.largoTotal;
      let recorrido = 0;
      for (let li = 0; li < c.lineas.length; li++) {
        if (recorrido >= objetivo) { break; }
        const parte = Math.min(1, (objetivo - recorrido) / c.largos[li]);
        const a = c.lineas[li][0];
        const b = c.lineas[li][1];
        const x1 = ox + c.px[a];
        const y1 = oy + c.py[a];
        ctx.moveTo(x1, y1);
        ctx.lineTo(x1 + (ox + c.px[b] - x1) * parte, y1 + (oy + c.py[b] - y1) * parte);
        recorrido += c.largos[li];
      }
      ctx.stroke();
      ctx.shadowBlur = 0;

      // --- estrellas: cada una se enciende cuando el trazo la alcanza ---
      for (let i = 0; i < c.px.length; i++) {
        const nace = Math.min(1, (avance - c.aparece[i]) / 0.06);
        if (nace <= 0) { continue; }
        const alfa = nace * alfaGrupo * (0.76 + 0.24 * Math.sin(t * c.titileos[i] + c.fases[i]));
        const x = ox + c.px[i];
        const y = oy + c.py[i];
        ctx.beginPath();
        ctx.arc(x, y, c.radios[i] * (2.3 + chispa * 1.6), 0, 6.2832);
        ctx.fillStyle = 'rgba(' + rgb + ', ' + ((0.18 + chispa * 0.22) * alfa).toFixed(3) + ')';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, c.radios[i], 0, 6.2832);
        ctx.fillStyle = paleta.constelacionNucleo + (0.95 * alfa).toFixed(3) + ')';
        ctx.fill();
      }

      // El nombre sólo cuando la figura ya está completa: antes sería
      // ponerle título a un garabato a medias.
      if (avance >= 1) {
        ctx.fillStyle = 'rgba(' + rgb + ', ' + (alfaGrupo * (0.42 + chispa * 0.35)).toFixed(3) + ')';
        ctx.fillText(c.nombre, ox, oy + c.etiquetaY);
      }
    }

    this.fugazPaso(ctx, t, dt, w, h);

    this.animationId = requestAnimationFrame(this.animate);
  };

  /**
   * Cometas que cruzan el cielo. Cada uno es una linea con degradado y una
   * cabeza brillante: mucho efecto por el precio de dos trazos. Hasta tres a
   * la vez, y el primero sale de inmediato para que el efecto se note desde
   * que abre la pantalla.
   */
  private fugazPaso(ctx: CanvasRenderingContext2D, t: number, dt: number, w: number, h: number) {
    const paleta = this.paleta;
    if (t >= this.proximaFugaz && this.fugaces.length < 3) {
      // Mitad de las veces cruza hacia la derecha, mitad hacia la izquierda,
      // siempre cayendo: hacia arriba no se ve natural.
      const haciaLaDerecha = Math.random() < 0.5;
      const ang = Math.PI * (0.12 + Math.random() * 0.2);
      const vel = 8 + Math.random() * 6;
      this.fugaces.push({
        x: haciaLaDerecha ? -60 + Math.random() * w * 0.4 : w * 0.6 + Math.random() * (w * 0.4 + 60),
        y: Math.random() * h * 0.45,
        vx: Math.cos(ang) * vel * (haciaLaDerecha ? 1 : -1),
        vy: Math.sin(ang) * vel,
        vida: 1,
      });
      this.proximaFugaz = t + 2500 + Math.random() * 3500;
    }

    for (let i = this.fugaces.length - 1; i >= 0; i--) {
      const f = this.fugaces[i];
      f.x += f.vx * dt;
      f.y += f.vy * dt;
      f.vida -= 0.011 * dt;
      if (f.vida <= 0 || f.x < -140 || f.x > w + 140 || f.y > h + 140) {
        this.fugaces.splice(i, 1);
        continue;
      }
      // En el ultimo tramo de vida se desvanece, en vez de cortarse en seco.
      const brillo = f.vida < 0.3 ? f.vida / 0.3 : 1;
      const colaX = f.x - f.vx * 13;
      const colaY = f.y - f.vy * 13;
      const cola = ctx.createLinearGradient(colaX, colaY, f.x, f.y);
      cola.addColorStop(0, paleta.fugazCola);
      cola.addColorStop(1, paleta.fugaz + (0.8 * brillo).toFixed(3) + ')');
      ctx.strokeStyle = cola;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(colaX, colaY);
      ctx.lineTo(f.x, f.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(f.x, f.y, 1.7, 0, 6.2832);
      ctx.fillStyle = paleta.fugazCabeza + (0.9 * brillo).toFixed(3) + ')';
      ctx.fill();
    }
  }

  /* ===== Login logic (unchanged) ===== */
  onSignin(form: NgForm) {
    const usuario = form.value.usuario;
    const password = form.value.password;
    if (!usuario) {
      this.alertaLogin('warning', 'Falta tu usuario', 'Por favor ingresa tu nombre de usuario.')
        .then(() => this.txtUser?.nativeElement?.focus());
      return;
    }
    if (!password) {
      this.alertaLogin('warning', 'Falta tu contraseña', 'Por favor ingresa tu contraseña.')
        .then(() => this.txtPass?.nativeElement?.focus());
      return;
    }
    this.guardarRecordado(usuario, password);
    this.authService.loginUser(usuario, password);
  }

  private alertaLogin(icon: 'warning' | 'error', titulo: string, mensaje: string) {
    return swal.fire({
      icon: icon,
      title: titulo,
      text: mensaje,
      confirmButtonText: 'Entendido',
      buttonsStyling: false,
      customClass: { popup: 'swal-dark', confirmButton: 'swal-dark__btn' },
    });
  }

  salir(){
    this.authService.logout();
  }

  recuperarPass(){

    const diaConfig = new MatDialogConfig()
    diaConfig.disableClose = false;
    diaConfig.autoFocus = true;
    diaConfig.width='460px';
    diaConfig.panelClass = "custom-modalbox";
    // El velo de siempre mas login-velo, con el que el cristal claro lo tine
    // del tono (modalEmail.component.css). En los demas estilos no cambia.
    diaConfig.backdropClass = ['cdk-overlay-dark-backdrop', 'login-velo'];

    let dialogRef = this.dialog.open(ModalEmailComponent,diaConfig);

  }

  showNotification(from: any, align: any,mensaje:any) {
    const type = ['', 'info', 'success', 'warning', 'danger', 'rose', 'primary'];
    const color=4;
    $.notify({
        icon: 'notifications',
        message: mensaje
    }, {
        type: type[color],
        timer: 3000,
        placement: {
            from: from,
            align: align
        },
        template: '<div data-notify="container" class="col-xs-11 col-sm-3 alert alert-{0} alert-with-icon" role="alert">' +
          '<button mat-raised-button type="button" aria-hidden="true" class="close" data-notify="dismiss">  <i class="material-icons">close</i></button>' +
          '<i class="material-icons" data-notify="icon">notifications</i> ' +
          '<span data-notify="title">{1}</span> ' +
          '<span data-notify="message">{2}</span>' +
          '<div class="progress" data-notify="progressbar">' +
            '<div class="progress-bar progress-bar-{0}" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100" style="width: 0%;"></div>' +
          '</div>' +
          '<a href="{3}" target="{4}" data-notify="url"></a>' +
        '</div>'
    });
}

}
